import React, { useEffect, useMemo, useState } from 'react';

// Step before the print files (TIFF): add other batches of the same quality to print together.
// Batches never change; only fabric not yet received can be printed; the same SKU in several batches becomes one
// file with the combined metres (buffer added once on that total, by the TIFF tool). Nothing is locked: a batch
// already sent shows "sent for printing on …" and can be added again (reprint of damaged fabric).
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const day = (t) => new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
const key = (batchId, sku) => `${batchId}|${String(sku).toUpperCase()}`;

// Same rows the files are made from: one per SKU, combined metres
export function mergeChosen(batches, chosen) {
  const bySku = new Map();
  batches.forEach((b) => b.lines.forEach((l) => {
    if (!chosen.has(key(b.id, l.sku))) return;
    const k = String(l.sku).toUpperCase();
    const row = bySku.get(k) || { sku: l.sku, fabricName: l.fabricName, totalQuantity: 0, parts: [] };
    row.totalQuantity = r2(row.totalQuantity + l.pending);
    row.parts.push({ batchId: b.id, sku: l.sku, metres: l.pending });
    bySku.set(k, row);
  }));
  return [...bySku.values()].sort((a, b) => String(a.fabricName).localeCompare(String(b.fabricName)) || a.sku.localeCompare(b.sku));
}

export default function PrintTogether({ api, batchId, onCancel, onReady }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [chosen, setChosen] = useState(() => new Set());
  const [open, setOpen] = useState(() => new Set());
  useEffect(() => {
    let alive = true;
    api(`/api/v1/order-desk/print-runs/candidates?batchId=${encodeURIComponent(batchId)}`).then((r) => {
      if (!alive) return;
      setData(r);
      const own = r.batches.find((b) => b.id === batchId);
      setChosen(new Set((own?.lines || []).map((l) => key(own.id, l.sku)))); // this batch: everything still to come
    }).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, batchId]);
  const merged = useMemo(() => (data ? mergeChosen(data.batches, chosen) : []), [data, chosen]);
  if (err) return <div className="od-pt"><p className="od-small od-late">{err}</p><button className="od-btn" onClick={onCancel}>Close</button></div>;
  if (!data) return <div className="od-pt"><div className="od-skel" aria-busy="true" /></div>;
  const toggleBatch = (b, on) => setChosen((p) => { const n = new Set(p); b.lines.forEach((l) => (on ? n.add(key(b.id, l.sku)) : n.delete(key(b.id, l.sku)))); return n; });
  const toggleLine = (b, l) => setChosen((p) => { const n = new Set(p); const k = key(b.id, l.sku); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const batchIds = [...new Set(merged.flatMap((m) => m.parts.map((p) => p.batchId)))];
  const total = r2(merged.reduce((t, m) => t + m.totalQuantity, 0));
  const multi = (data.qualities || []).length > 1; // a Mixed batch: show each fabric's quality
  const shared = merged.filter((m) => m.parts.length > 1).length; // fabrics in more than one batch → one file
  return (
    <div className="od-pt" aria-label="Print together">
      <div className="od-pt-h">
        <b>Print together</b>
        <span className="od-small">{data.quality} fabric still to come in open batches (Mixed PO batches show only their {data.quality} fabrics). Same fabric in several batches becomes one file with the total metres.</span>
      </div>
      <ul className="od-pt-list">
        {data.batches.map((b) => {
          const n = b.lines.filter((l) => chosen.has(key(b.id, l.sku))).length;
          const all = b.lines.length > 0 && n === b.lines.length;
          const m = r2(b.lines.reduce((t, l) => t + l.pending, 0));
          const isOpen = open.has(b.id);
          const chosenM = r2(b.lines.filter((l) => chosen.has(key(b.id, l.sku))).reduce((t, l) => t + l.pending, 0));
          return (
            <li key={b.id} className={n ? 'on' : ''}>
              {/* line 1: tick · batch · last printed | metres to come; line 2: fabrics · created | pick fabrics */}
              <div className="od-pt-b">
                <input type="checkbox" aria-label={`Print all of ${b.id}`} checked={all} ref={(el) => { if (el) el.indeterminate = n > 0 && !all; }} disabled={!b.lines.length} onChange={(e) => toggleBatch(b, e.target.checked)} />
                <b className="od-mono">{b.id}</b>
                {b.id === batchId && <span className="od-pt-this">this batch</span>}
                {/^mix/i.test(b.quality || '') && <span className="od-pt-q" title="Process by PO batch with several qualities">Mixed · {[...new Set(b.lines.map((l) => l.quality))].join(' + ') || data.quality} only</span>}
                {/* printed = print files made in the app, or fabric already came in (files made outside the app) */}
                <span className={`od-pt-sent${b.sentToPrintMs || b.receivedMetres > 0 ? '' : ' no'}`}>{b.sentToPrintMs ? `last printed ${day(b.sentToPrintMs)}${b.printCount > 1 ? ` · ${b.printCount}×` : ''}`
                  : b.receivedMetres > 0 ? `printed · ${b.receivedMetres} m in${b.firstPartMs ? ` since ${day(b.firstPartMs)}` : ''}` : 'not printed yet'}</span>
                {b.state === 'rest_overdue' && <span className="od-pt-late">Partial overdue</span>}
                {b.state === 'print_late' && <span className="od-pt-late">Print pending</span>}
                <span className="od-pt-m"><b>{n ? `${chosenM} m` : `${m} m`}</b> {n && !all ? `of ${m} m` : 'to come'}</span>
              </div>
              <div className="od-pt-sub">
                <span className="od-small">{b.lines.length ? `${n && !all ? `${n} of ` : ''}${b.lines.length} fabric${b.lines.length === 1 ? '' : 's'}` : 'nothing left to print'}{b.createdMs ? ` · created ${day(b.createdMs)}` : ''}</span>
                {b.lines.length > 0 && <button className="od-pt-pick" aria-expanded={isOpen} onClick={() => setOpen((p) => { const x = new Set(p); if (x.has(b.id)) x.delete(b.id); else x.add(b.id); return x; })}>{isOpen ? 'Hide fabrics' : 'Pick fabrics'}</button>}
              </div>
              {isOpen && (
                <div className="od-pt-lines">
                  {b.lines.map((l) => (
                    <label key={l.sku} className="od-pt-line">
                      <input type="checkbox" checked={chosen.has(key(b.id, l.sku))} onChange={() => toggleLine(b, l)} />
                      <span className="od-mono">{l.sku}</span>
                      <span className="od-small">{l.fabricName}</span>
                      {multi && l.quality && <span className="od-pt-q">{l.quality}</span>}
                      <span className="od-small od-pt-m">{l.pending} m{l.received > 0 ? ` (${l.received} of ${l.metres} in)` : ''}</span>
                    </label>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="od-pt-foot">
        <span className="od-pt-sum">{merged.length ? <><b>{merged.length} file{merged.length === 1 ? '' : 's'} · {total} m</b><small>{batchIds.length} batch{batchIds.length === 1 ? '' : 'es'}{shared ? ` · ${shared} fabric${shared === 1 ? '' : 's'} in more than one batch, printed as one file` : ''} · print extra added per file</small></> : <b>Nothing chosen</b>}</span>
        <button className="od-btn" onClick={onCancel}>Cancel</button>
        <button className="od-btn od-pri" disabled={!merged.length} onClick={() => onReady({ items: merged, parts: merged.flatMap((m) => m.parts), batchIds, quality: data.quality })}>Next: make print files</button>
      </div>
    </div>
  );
}
